import XLSX, { WorkBook, WorkSheet } from 'xlsx-js-style'

import { ImportOrdersModalIds } from '../../orderImportModal.ids'
import {
  AreMoreItemsThanSupportedType,
  HeadersAndRows,
  Row,
  TemplateConfigs,
  TemplateConstraint,
  ValidationResult,
} from '../ImportOrders.types'

const SHEET_NAME: string = 'Order Data'
const SHEET_NAME_STANDARD: string = 'Order Summary'
const NEW_SHEET_NAME_STANDARD: string = 'New Order Summary'
const RETAILER_TITLE: string = 'Retailer'
export const MAX_ITEMS_PER_FILE: number = 30000
export const MAX_FILE_SIZE = 4 * 1024 * 1024 // 4MB in bytes

/**
 * These constraints come from a hardcoded value inserted into
 * the integration_configs table in the data self serve database.
 */
export const TEMPLATE_CONSTRAINTS = [
  'color name',
  'color code',
  'upc',
  'sku identifier',
] as const

export const isTemplateConstraint = (
  maybeConstraint: string,
): maybeConstraint is TemplateConstraint =>
  (TEMPLATE_CONSTRAINTS as readonly string[]).includes(maybeConstraint)

const falseValidationResult: ValidationResult = {
  isValid: false,
  errorMsg: '',
  templateId: '',
}

export enum SimplifiedTypes {
  Standard = 'standard',
  Vertical = 'vertical',
  Horizontal = 'horizontal',
}

export const getTemplateOptions = (
  isLiteRetailer: boolean,
  templateConfigs: TemplateConfigs,
) => {
  const onlyVerticalUPC =
    Array.isArray(templateConfigs.vertical) &&
    templateConfigs.vertical.length === 1 &&
    templateConfigs.vertical[0].constraint === 'upc'

  const verticalOptionLabel = !templateConfigs[SimplifiedTypes.Vertical]
    ? undefined
    : !isLiteRetailer && onlyVerticalUPC
      ? 'JOOR Vertical UPC Template'
      : `JOOR Vertical Order Template${onlyVerticalUPC ? ' (UPC)' : ''}`

  return {
    Standard: {
      value: SimplifiedTypes.Standard,
      label: 'JOOR Standard Excel Order',
      id: ImportOrdersModalIds.StandardDropdownSelectionId,
    },
    ...(!!templateConfigs[SimplifiedTypes.Vertical] && {
      Vertical: {
        value: SimplifiedTypes.Vertical,
        label: verticalOptionLabel,
        id: ImportOrdersModalIds.VerticalDropdownSelectionId,
      },
    }),
    ...(!!templateConfigs[SimplifiedTypes.Horizontal] &&
      !isLiteRetailer && {
        Horizontal: {
          value: SimplifiedTypes.Horizontal,
          label: 'JOOR Horizontal Order Template',
          id: ImportOrdersModalIds.HorizontalDropdownSelectionId,
        },
      }),
  }
}

export const getTemplateLink = (
  type: string,
  isLiteRetailer: boolean,
  onlyVerticalUPC: boolean,
): string => {
  switch (type) {
    case SimplifiedTypes.Horizontal:
      return 'https://cdn.jooraccess.com/external/templates/order_import/Order-Import_Horizontal-Template_Sample.xlsx'

    case SimplifiedTypes.Vertical:
      return isLiteRetailer
        ? 'https://cdn.jooraccess.com/external/templates/order_import/Order-Import_Retailer-Template_UPC.xlsx'
        : onlyVerticalUPC
          ? 'https://cdn.jooraccess.com/external/templates/order_import/Order-Import_Vertical-Template_Sample_UPC.xlsx'
          : 'https://cdn.jooraccess.com/external/templates/order_import/Order-Import_Vertical-Template_Sample.xlsx'

    default:
      return ''
  }
}

export const invalid = (message: string): ValidationResult => ({
  ...falseValidationResult,
  errorMsg: message,
})

export const validateFile = async (
  file: File,
  templateType: SimplifiedTypes,
  templateConfigs: TemplateConfigs,
): Promise<ValidationResult> => {
  let parsedFile: WorkBook

  try {
    const data = await file.arrayBuffer()
    parsedFile = XLSX.read(data)
  } catch {
    return invalid('There was an error parsing the file. Try upload it again.')
  }

  return validateTemplate(parsedFile, templateType, templateConfigs)
}

export const validateTemplate = (
  workbook: XLSX.WorkBook,
  templateType: SimplifiedTypes,
  templateConfigs: TemplateConfigs,
): ValidationResult => {
  if (!workbook) {
    return invalid('There is not a valid workbook')
  }

  let workSheet: XLSX.WorkSheet =
    workbook.SheetNames.length === 1
      ? workbook.Sheets[workbook.SheetNames[0]]
      : workbook.Sheets[SHEET_NAME]

  if (!workSheet?.A1) {
    return invalid(`The workbook doesn't contain a valid worksheet`)
  }

  const { headers, rows } = getHeaderAndRows(workSheet)

  if (!rows.length) {
    return invalid(`File must contain at least 1 row with data`)
  }

  return validateHeaderAndRows({
    headers,
    rows,
    templateType,
    templateConfigs,
  })
}

export const validateStandardFiles = async (
  files: File[],
  selectedFiles: File[],
): Promise<ValidationResult> => {
  const validationResult = {
    isValid: true,
    type: 'standard',
    errorMsg: '',
    templateId: '',
    retailerName: '',
  }

  if (selectedFiles.length + files.length > 10) {
    validationResult.errorMsg =
      'It is not possible to handle more than 10 files at once.'
    validationResult.isValid = false
    return validationResult
  }

  await Promise.all(
    files.map(async (file) => {
      if (validationResult.errorMsg) {
        return
      }

      try {
        const data = await file.arrayBuffer()
        const parsedFile = XLSX.read(data)
        validateStandardFile({
          parsedFile,
          selectedFiles,
          file,
          validationResult,
        })
      } catch {
        validationResult.errorMsg =
          'There was an error parsing the file. Try upload it again.'
      }
    }),
  )

  if (validationResult.errorMsg) {
    validationResult.isValid = false
  }

  return validationResult
}

export const validateStandardFile = ({
  parsedFile,
  selectedFiles,
  file,
  validationResult,
}: {
  parsedFile: WorkBook
  selectedFiles: File[]
  file: File
  validationResult: ValidationResult
}) => {
  let workSheet = parsedFile!.Sheets[SHEET_NAME_STANDARD]
  if (!workSheet) {
    workSheet = parsedFile!.Sheets[NEW_SHEET_NAME_STANDARD]
  }
  const doorFound = checkIfIncludeDoorStandard(
    parsedFile!.Sheets[parsedFile!.SheetNames[0]],
  )

  validationResult[doorFound ? 'includeDoor' : 'oneFileNoDoor'] = true

  const retailerTitle = workSheet?.A1?.v
  const retailerName = workSheet?.B1?.v

  const fileAlreadyPresent =
    selectedFiles &&
    selectedFiles?.reduce(
      (alreadyPresent, selectedFile) =>
        selectedFile.name === file.name || alreadyPresent,
      false,
    )

  if (fileAlreadyPresent) {
    validationResult.errorMsg = `There is already a file with the name ${file.name}`
  }

  if (!workSheet) {
    validationResult.errorMsg = `The workbook has to contain either a Sheet called 
    "${SHEET_NAME_STANDARD}" or "${NEW_SHEET_NAME_STANDARD}"`
  }

  if (retailerTitle !== RETAILER_TITLE || !retailerName) {
    return
  }

  if (
    validationResult.retailerName &&
    validationResult.retailerName !== retailerName
  ) {
    validationResult.errorMsg =
      'There are different retailers in different files.'
  } else validationResult.retailerName = retailerName
}

const validateHeaderAndRows = ({
  headers,
  rows,
  templateType,
  templateConfigs,
}: HeadersAndRows): ValidationResult => {
  const filteredHeaders = (headers as (string | null)[]).filter(
    (header): header is string => typeof header === 'string',
  )

  const matchingTemplate = templateConfigs[
    templateType as keyof TemplateConfigs
  ]?.find((template) =>
    template.requiredColumns.every((col: string) =>
      filteredHeaders.includes(col),
    ),
  )

  if (!matchingTemplate) {
    return invalid("The uploaded file doesn't match the selected template.")
  }

  const uniqueElements = new Set()
  const duplicates: string[] = []
  filteredHeaders.forEach((column: string) => {
    if (uniqueElements.has(column) && duplicates.indexOf(column) === -1) {
      duplicates.push(column)
    } else {
      uniqueElements.add(column)
    }
  })

  if (duplicates?.length > 0) {
    return invalid('Duplicated column headers: ' + duplicates.join(', '))
  }

  const numItems = countItems({
    headers: filteredHeaders,
    rows,
    templateType,
    matchingTemplate,
  })

  if (numItems > MAX_ITEMS_PER_FILE) {
    return invalid(`File must contain less than ${MAX_ITEMS_PER_FILE} items`)
  }

  return {
    isValid: true,
    templateId: matchingTemplate.id,
    type: matchingTemplate.name,
    errorMsg: '',
    includeDoor: checkIfIncludeDoor(filteredHeaders),
  }
}

const getHeaderAndRows = (
  workSheet: WorkSheet,
): { headers: string[] | null[]; rows: Row[] } => {
  const dataObject = XLSX.utils.sheet_to_json<Row>(workSheet, {
    blankrows: false,
    header: 1,
    defval: null,
  })

  const headers = dataObject[0]
  const rows = dataObject.splice(1)
  return { headers, rows }
}

const checkIfIncludeDoor = (header: string[]): boolean =>
  header.find((cell: string | null) =>
    String(cell).toLowerCase().includes('door'),
  ) !== undefined

const checkIfIncludeDoorStandard = (worksheet: XLSX.WorkSheet): boolean => {
  const cellValues = XLSX.utils.sheet_to_json<Row>(worksheet, { header: 1 })
  return cellValues.some((row: Row) =>
    row.some(
      (cell: string | null) =>
        typeof cell === 'string' && cell.includes('Door 1'),
    ),
  )
}

const countItems = ({
  headers,
  rows,
  templateType,
  matchingTemplate,
}: AreMoreItemsThanSupportedType): number => {
  if (templateType === SimplifiedTypes.Vertical) {
    return rows.length
  }

  return headers.reduce((counter, header, index) => {
    if (!header || matchingTemplate.allColumns.indexOf(header) >= 0) {
      return counter
    }

    let partialItemsNumber = 0
    for (let i = 0; i < rows.length; i++) {
      let value = rows[i][index]

      if (typeof value === 'number' && value >= 0) {
        partialItemsNumber++
      } else {
        partialItemsNumber = 0
        break
      }
    }

    counter += partialItemsNumber
    return counter
  }, 0)
}

export const dropErrorsMessageMap: Record<string, string> = {
  'file-invalid-type':
    'Invalid file type, only Excel or CSV files are allowed.',
  'file-too-large': `File exceeds maximum size of 4MB.`,
}
